import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header';
import { CONTACT } from 'config/contact';
import Footer from '../../components/Footer';
import ContactHero from './components/ContactHero';
import ContactMethods from './components/ContactMethods';
import ConsultationBenefits from './components/ConsultationBenefits';
import ConsultationProcess from './components/ConsultationProcess';
import ContactForm from './components/ContactForm';
import TrustSignals from './components/TrustSignals';
import FAQSection from '../services-overview/components/FAQSection';
import type { ContactMethod, ConsultationBenefit, ProcessStep, TrustSignal, FAQ, ContactFormData } from './types';
import NewsletterSignup from '../../components/NewsletterSignup';
import GoogleReviewsSection from '../../components/GoogleReviewsSection';
import { supabase } from '../../lib/supabase';


const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCalendly, setShowCalendly] = useState(false);

  const contactMethods: ContactMethod[] = [
    {
      id: 'calendly',
      name: 'Book Consultation',
      icon: 'Calendar',
      description: 'Schedule a free 30-minute automation audit at your convenience',
      action: 'Book Now',
      available: true
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp Chat',
      icon: 'MessageCircle',
      description: 'Get instant responses to your automation questions',
      action: 'Start Chat',
      available: true
    },
    {
      id: 'email',
      name: 'Email Us',
      icon: 'Mail',
      description: 'Send us a detailed message and we will respond within 2 hours',
      action: 'Send Email',
      available: true
    },
    {
      id: 'phone',
      name: 'Phone Call',
      icon: 'Phone',
      description: 'Speak directly with our automation experts',
      action: 'Call Now',
      available: true
    }
  ];

  const consultationBenefits: ConsultationBenefit[] = [
    {
      id: 1,
      icon: 'Target',
      title: 'Process Analysis',
      description: 'We will identify your most time-consuming manual processes and quantify the hours you are losing each month.'
    },
    {
      id: 2,
      icon: 'Lightbulb',
      title: 'Custom Recommendations',
      description: 'Get specific automation strategies tailored to your business, industry, and current tech stack.'
    },
    {
      id: 3,
      icon: 'TrendingUp',
      title: 'ROI Projection',
      description: 'See exactly how much time and money you could save with automation, backed by real data and case studies.'
    },
    {
      id: 4,
      icon: 'Zap',
      title: 'Quick Wins',
      description: 'Walk away with 2-3 automation ideas you can implement immediately, even without our help.'
    },
    {
      id: 5,
      icon: 'FileText',
      title: 'Implementation Roadmap',
      description: 'Receive a prioritized action plan showing which automations to tackle first for maximum impact.'
    },
    {
      id: 6,
      icon: 'Users',
      title: 'Expert Guidance',
      description: 'Learn from automation specialists who have helped businesses optimize their operations.'
    }
  ];

  const processSteps: ProcessStep[] = [
    {
      id: 1,
      number: '01',
      title: 'Discovery Call',
      description: 'We learn about your business, current processes, and automation goals',
      duration: '10 min'
    },
    {
      id: 2,
      number: '02',
      title: 'Process Review',
      description: 'We analyze your workflows and identify automation opportunities',
      duration: '10 min'
    },
    {
      id: 3,
      number: '03',
      title: 'Strategy Session',
      description: 'We present custom recommendations with ROI projections',
      duration: '8 min'
    },
    {
      id: 4,
      number: '04',
      title: 'Action Plan',
      description: 'You receive a prioritized roadmap and next steps',
      duration: '2 min'
    }
  ];

  const trustSignals: TrustSignal[] = [
    {
      id: 1,
      icon: 'Shield',
      title: 'SSL Encrypted',
      description: 'All data transmitted through secure 256-bit SSL encryption'
    },
    {
      id: 2,
      icon: 'Lock',
      title: 'GDPR Compliant',
      description: 'Full compliance with data protection regulations'
    },
    {
      id: 3,
      icon: 'CheckCircle',
      title: 'Privacy Protected',
      description: 'Your information is never shared with third parties'
    },
    {
      id: 4,
      icon: 'Award',
      title: 'Certified Partners',
      description: 'Official partners with n8n, Make.com, and OpenAI'
    }
  ];

  const faqs: FAQ[] = [
    {
      id: 1,
      question: 'Is the automation audit really free?',
      answer: 'Yes, absolutely! The 30-minute consultation is completely free with no strings attached. We believe in providing value upfront. You will walk away with actionable insights regardless of whether you decide to work with us. There is no credit card required, no hidden fees, and no obligation to purchase anything.'
    },
    {
      id: 2,
      question: 'What should I prepare for the consultation?',
      answer: 'Come prepared to discuss your current business processes, particularly the repetitive tasks that consume the most time. It is helpful to have a general idea of your monthly time spent on manual tasks, but do not worry if you do not have exact numbers. We will help you identify and quantify these during the call. Having access to your current tools and workflows will also be beneficial.'
    },
    {
      id: 3,
      question: 'How long does the audit take?',
      answer: 'The consultation is designed to be efficient and valuable, lasting exactly 30 minutes. We respect your time and have structured the process to deliver maximum insights in minimal time. If we identify complex opportunities that require deeper discussion, we can always schedule a follow-up call.'
    },
    {
      id: 4,
      question: 'Will you try to sell me something during the call?',
      answer: 'No high-pressure sales tactics, we promise. Our goal is to provide genuine value and help you understand your automation opportunities. If our services are a good fit and you are interested in working together, we will discuss options. But many businesses implement our recommendations on their own, and we are perfectly fine with that. The audit is about education, not sales.'
    },
    {
      id: 5,
      question: 'What if I am not tech-savvy?',
      answer: 'That is perfectly fine! We specialize in explaining technical concepts in simple, business-focused terms. You do not need any technical knowledge to benefit from the consultation. We will focus on the business impact and outcomes, not the technical jargon. Our goal is to make automation accessible and understandable for everyone.'
    },
    {
      id: 6,
      question: 'Can I implement the recommendations myself?',
      answer: 'Absolutely! We will provide you with clear, actionable recommendations that you can implement on your own if you have the time and resources. Some businesses prefer to handle automation internally, and we fully support that. We are here to help whether you need full implementation support or just strategic guidance.'
    },
    {
      id: 7,
      question: 'What happens after the consultation?',
      answer: 'After the call, you will receive a summary email with the key insights, recommendations, and ROI projections we discussed. You are free to implement these on your own, reach out with questions, or discuss working together if you would like our help. There is no pressure or follow-up unless you request it.'
    },
    {
      id: 8,
      question: 'Do you work with businesses in my industry?',
      answer: 'We work with businesses across various industries including e-commerce, real estate, consulting, digital agencies, SaaS, healthcare, and finance. While automation principles are universal, we tailor our recommendations to your specific industry challenges and opportunities. During the consultation, we will share relevant case studies from similar businesses.'
    }
  ];

  const handleBookAudit = () => {
    setShowCalendly(true);
    window.open('https://calendly.com/makemind-automations', '_blank');
  };

  const handleMethodSelect = (method: ContactMethod) => {
    switch (method.id) {
      case 'calendly':
        handleBookAudit();
        break;
      case 'whatsapp':
        window.open(CONTACT.whatsappLink, '_blank');
        break;
      case 'email':
        window.location.href = 'mailto:PK468080@gmail.com';
        break;
      case 'phone':
        window.location.href = CONTACT.telLink;
        break;
    }
  };

   const handleFormSubmit = async (data: ContactFormData) => {
  setIsSubmitting(true);

  // Build a single message string with all extra fields
  const fullMessage = `
Company: ${data.company || '-'}
Company size: ${data.companySize || '-'}
Industry: ${data.industry || '-'}
Automation goals: ${data.automationGoals?.join(', ') || '-'}
Current challenges: ${data.currentChallenges || '-'}
Monthly time spent on manual work: ${data.monthlyTimeSpent || '-'}
Preferred contact method: ${data.preferredContactMethod || '-'}
Urgency: ${data.urgency || '-'}
Heard about us from: ${data.hearAboutUs || '-'}
Additional notes: ${data.additionalNotes || '-'}
`.trim();

  try {
    const { error } = await supabase.from('leads').insert({
      name: data.fullName,
      email: data.email,
      phone: data.phone,
      message: fullMessage,
      source_page: window.location.pathname,
      // status defaults to "new" from SQL
    });

    if (error) {
      console.error('Lead insert error:', error);
      alert('❌ There was an error submitting your details: ' + error.message);
    } else {
      alert('✅ Thank you! We have received your details and will contact you shortly.');
    }
  } catch (err: any) {
    console.error('Unexpected error:', err);
    alert('❌ Unexpected error: ' + err.message);
  } finally {
    setIsSubmitting(false);
  }
};


  return (
    <>
      <Helmet>
        <title>Contact Us - Book Free Automation Audit | Make Mind Automations</title>
        <meta
          name="description"
          content="Book a free 30-minute automation audit and discover how to reclaim 40-70 hours monthly. No sales pressure, just strategic insights tailored to your business. Contact Make Mind Automations today."
        />
        <meta
          name="keywords"
          content="automation consultation, free audit, business automation, contact automation experts, book consultation, automation strategy"
        />
        <meta property="og:title" content="Contact Us - Book Free Automation Audit | Make Mind Automations" />
        <meta
          property="og:description"
          content="Book a free 30-minute automation audit and discover how to reclaim 40-70 hours monthly. No commitment required."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.makemindautomations.tech/contact" />
        <link
  rel="canonical"
  href="https://www.makemindautomations.tech/contact"
/>

      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main>
          <ContactHero onBookAudit={handleBookAudit} />
          <ContactMethods methods={contactMethods} onMethodSelect={handleMethodSelect} />
          <ConsultationBenefits benefits={consultationBenefits} />
          <ConsultationProcess steps={processSteps} />
          <ContactForm onSubmit={handleFormSubmit} isSubmitting={isSubmitting} />
          <NewsletterSignup variant="default" />
          <GoogleReviewsSection />
          <TrustSignals signals={trustSignals} />
          <FAQSection faqs={faqs} />
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Contact;